import type { AssignableItem } from 'pages/custom_tests/components/assign_wizard/AssignWizard';
import { useAssignWizard } from 'pages/custom_tests/components/assign_wizard/context';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { Badge, Button, Form, Nav } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

export const TargetsStep = () => {

    const { accessCodes, activeTab, classrooms, currentUserId, initialAssignedAccessCodeIds, initialAssignedClassroomIds, loadError,
        loading, myClassroomsOnly, requiredLicenses, retryLoad, searchTerm, selectedAccessCodes, selectedClassrooms, setActiveTab,
        setMyClassroomsOnly, setSearchTerm, toggleAccessCode, toggleClassroom } = useAssignWizard();
    const { formatMessage } = useIntl();

    const matchesSearch = (item: AssignableItem) => {

        const term = searchTerm.trim().toLowerCase();
        if (!term) return true;
        return `${item.Name ?? ''} ${item.Identifier ?? ''}`.toLowerCase().includes(term);

    };

    // Expired targets stay visible (dimmed, non-selectable) per the design mock — hiding
    // them would leave users unable to see why a code or classroom is unavailable.
    const visibleClassrooms = classrooms
        .filter(item => !myClassroomsOnly || item.OwnerUserId === currentUserId)
        .filter(matchesSearch);

    const visibleAccessCodes = accessCodes
        .filter(matchesSearch);

    const isClassroomSelected = (item: AssignableItem) => selectedClassrooms.some(({ Id }) => Id === item.Id);

    const isAccessCodeSelected = (item: AssignableItem) => selectedAccessCodes.some(({ Id }) => Id === item.Id);

    // Programs on the test that this access code holds NO license for (name compare, trimmed
    // and case-insensitive). Programs with an empty license list are excluded — no active
    // license grants them at all, so no code could cover them.
    const getUncoveredPrograms = (item: AssignableItem) => {

        const held = (item.Licenses ?? '').split(',').map(name => name.trim().toLowerCase()).filter(Boolean);

        return requiredLicenses.filter(program => program.Licenses.length > 0
            && !program.Licenses.some(({ Name }) => held.includes(Name.trim().toLowerCase())));

    };

    const renderAlreadyAssignedBadge = () => (
        <Badge bg={'secondary'} className={'ms-2'}>
            <FormattedMessage defaultMessage="Already assigned" id="already_assigned.6f1c0daf" />
        </Badge>
    );

    const renderClassroomRow = (item: AssignableItem) => {

        const expired = !!item.Expired;
        const locked = initialAssignedClassroomIds.includes(item.Id);
        const selected = isClassroomSelected(item);

        return (
            <label
                className={`assign-target-row border rounded px-3 py-2 d-flex align-items-start gap-3 mb-2 ${expired ? 'opacity-50' : locked ? 'opacity-75' : selected ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                htmlFor={`classroom-${item.Id}`}
                key={item.Id}
                role={locked || expired ? undefined : 'button'}
            >
                {/* Toggle on click, not change: clicking an already-checked radio never
                    fires onChange, which would make the deselect branch unreachable. */}
                <Form.Check
                    checked={selected}
                    className={'mt-1'}
                    disabled={locked || expired}
                    id={`classroom-${item.Id}`}
                    onChange={() => undefined}
                    onClick={() => !locked && !expired && toggleClassroom(item)}
                    type={'radio'}
                />
                <div className={'flex-fill'}>
                    <div className={'d-flex justify-content-between align-items-start gap-2'}>
                        <span className={'fw-bold'}>
                            {item.Name}
                            {locked ? renderAlreadyAssignedBadge() : null}
                        </span>
                        {item.Identifier ? (
                            <span className={'small text-muted'}>
                                {`${formatMessage({ id: 'identifier.45eac7f9', defaultMessage: 'Identifier' })}: ${item.Identifier}`}
                            </span>
                        ) : null}
                    </div>
                    <div className={'d-flex justify-content-between align-items-end gap-2'}>
                        {item.InstructorString ? (
                            <span className={'small text-muted'}>
                                {`${formatMessage({ id: 'instructors.24923680', defaultMessage: 'Instructors' })}: ${item.InstructorString}`}
                            </span>
                        ) : <span />}
                        <span className={'small text-muted fw-semibold'}>
                            {`${formatMessage({ id: 'students.0933e5a0', defaultMessage: 'Students' })}: ${item.StudentCount ?? 0}`}
                        </span>
                    </div>
                    {expired ? (
                        <div className={'small text-warning mt-1'}>
                            <i className={'fa-solid fa-triangle-exclamation me-1'} />
                            <FormattedMessage defaultMessage="Classroom Expired - Students cannot access" id="classroom_expired_students_cannot_access.e7746bef" />
                        </div>
                    ) : null}
                </div>
            </label>
        );

    };

    const renderAccessCodeRow = (item: AssignableItem) => {

        const expired = !!item.Expired;
        const locked = initialAssignedAccessCodeIds.includes(item.Id);
        const selected = isAccessCodeSelected(item);
        const uncoveredPrograms = getUncoveredPrograms(item);
        const uncoveredNames = uncoveredPrograms.flatMap(({ Licenses }) => Licenses.map(({ Name }) => Name));

        return (
            <label
                className={`assign-target-row border rounded px-3 py-2 d-flex align-items-start gap-3 mb-2 ${expired ? 'opacity-50' : locked ? 'opacity-75' : selected ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                htmlFor={`access-code-${item.Id}`}
                key={item.Id}
                role={locked || expired ? undefined : 'button'}
            >
                {/* Toggle on click, not change: clicking an already-checked radio never
                    fires onChange, which would make the deselect branch unreachable. */}
                <Form.Check
                    checked={selected}
                    className={'mt-1'}
                    disabled={locked || expired}
                    id={`access-code-${item.Id}`}
                    onChange={() => undefined}
                    onClick={() => !locked && !expired && toggleAccessCode(item)}
                    type={'radio'}
                />
                <div className={'flex-fill'}>
                    <div className={'d-flex justify-content-between align-items-start gap-2'}>
                        <span className={'fw-bold'}>
                            {item.Name}
                            {locked ? renderAlreadyAssignedBadge() : null}
                        </span>
                        {item.Identifier ? (
                            <span className={'small text-muted text-nowrap'}>
                                {`${formatMessage({ id: 'identifier.45eac7f9', defaultMessage: 'Identifier' })}: ${item.Identifier}`}
                            </span>
                        ) : null}
                    </div>
                    {item.Licenses ? (
                        <div className={'small text-muted mt-1'}>
                            <span className={'fw-semibold'}>
                                {`${formatMessage({ id: 'licenses.11b1c36a', defaultMessage: 'Licenses' })}: `}
                            </span>
                            {item.Licenses}
                        </div>
                    ) : null}
                    {uncoveredNames.length ? (
                        <div
                            title={uncoveredPrograms
                                .map(({ Licenses, ProductName }) => `${ProductName}: ${Licenses.map(({ Name }) => Name).join(', ')}`)
                                .join('\n')}
                            className={'small text-warning mt-1'}
                        >
                            {`${formatMessage({ id: 'missing_license.8ec7bd69', defaultMessage: 'Missing license:' })} ${uncoveredNames[0]}${uncoveredNames.length > 1 ? ` +${uncoveredNames.length - 1}` : ''}`}
                        </div>
                    ) : null}
                    <div className={'d-flex flex-wrap gap-4 small text-muted mt-1'}>
                        <span>
                            <span className={'fw-semibold'}>
                                {`${formatMessage({ id: 'uses_left.aae1b7d3', defaultMessage: 'Uses Left' })}: `}
                            </span>
                            {item.UsesRemaining ?? 0}
                        </span>
                        <span>
                            <span className={'fw-semibold'}>
                                {`${formatMessage({ id: 'students.0933e5a0', defaultMessage: 'Students' })}: `}
                            </span>
                            {item.StudentCount ?? 0}
                        </span>
                        {item.ExpirationDateString ? (
                            <span>
                                <span className={'fw-semibold'}>
                                    {`${formatMessage({ id: 'expires.84d49534', defaultMessage: 'Expires' })}: `}
                                </span>
                                {item.ExpirationDateString}
                            </span>
                        ) : null}
                    </div>
                    {expired ? (
                        <div className={'small text-warning mt-1'}>
                            <i className={'fa-solid fa-triangle-exclamation me-1'} />
                            <FormattedMessage defaultMessage="Code Expired - Students cannot access" id="code_expired_students_cannot_access.feb3d442" />
                        </div>
                    ) : null}
                </div>
            </label>
        );

    };

    const activeItems = activeTab === 'accessCodes' ? visibleAccessCodes : visibleClassrooms;
    const emptyText = activeTab === 'accessCodes'
        ? (formatMessage({ id: 'no_access_codes_found.2ac9ae6b', defaultMessage: 'No access codes found.' }))
        : (formatMessage({ id: 'no_classrooms_found.60f375dc', defaultMessage: 'No classrooms found.' }));

    return (
        <div className={'assign-targets-step d-flex flex-column gap-3'}>

            <Nav
                activeKey={activeTab}
                className={'assign-target-tabs'}
                onSelect={key => setActiveTab((key as 'classrooms' | 'accessCodes') ?? 'classrooms')}
                variant={'tabs'}
            >
                <Nav.Item>
                    <Nav.Link eventKey={'classrooms'}>
                        <FormattedMessage defaultMessage="Classrooms" id="classrooms.e66138fb" />
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey={'accessCodes'}>
                        <FormattedMessage defaultMessage="Access Codes" id="access_codes.3114ae04" />
                    </Nav.Link>
                </Nav.Item>
            </Nav>

            <div className={'d-flex align-items-center gap-3'}>
                <Form.Control
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder={formatMessage({ id: 'search_by_name_or_identifier.9b7fdfd4', defaultMessage: 'Search by name or identifier' })}
                    value={searchTerm}
                />
                {activeTab === 'classrooms' && (
                    <div className={'d-flex flex-column align-items-start text-nowrap'}>
                        <Form.Label className={'small text-muted mb-1'} htmlFor={'my-classrooms-toggle'}>
                            <FormattedMessage defaultMessage="My Classrooms" id="my_classrooms.c608bc1e" />
                        </Form.Label>
                        <Form.Check
                            checked={myClassroomsOnly}
                            id={'my-classrooms-toggle'}
                            onChange={e => setMyClassroomsOnly(e.target.checked)}
                            type={'switch'}
                        />
                    </div>
                )}
            </div>

            {loading ?
                (
                    <div className={'d-flex align-items-center justify-content-center p-5'}>
                        <GMetrixLoader />
                    </div>
                )
                : loadError ?
                    (
                        <div className={'alert alert-danger d-flex align-items-center justify-content-between gap-3 mb-0'}>
                            <span>
                                {loadError}
                            </span>
                            <Button onClick={retryLoad} size={'sm'} variant={'outline-danger'}>
                                <FormattedMessage defaultMessage="Retry" id="retry.225ca180" />
                            </Button>
                        </div>
                    )
                    : activeItems.length === 0 ?
                        (
                            <div className={'d-flex align-items-center justify-content-center text-muted border rounded p-5'}>
                                {emptyText}
                            </div>
                        )
                        :
                        (
                            <div className={'assign-target-list'} style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                                {activeTab === 'accessCodes'
                                    ? visibleAccessCodes.map(renderAccessCodeRow)
                                    : visibleClassrooms.map(renderClassroomRow)}
                            </div>
                        )}

        </div>
    );

};
